/**
 * Tiers Management Page
 * Allows authors to manage their subscription tiers
 */
import {
  useState,
  useEffect,
  useCallback,
  type FormEvent,
  type ChangeEvent,
} from "react";
import { api, ApiError, type Tier, type BillingPeriod } from "../../lib/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TierFormData {
  name: string;
  slug: string;
  description: string;
  level: number;
  priceDollars: string;
  billingPeriod: BillingPeriod;
}

interface TierFormErrors {
  name?: string;
  slug?: string;
  level?: string;
  priceDollars?: string;
}

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

/**
 * Convert a name to a URL-safe slug
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Format cents as dollar string
 */
function formatPrice(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Parse dollar string to cents
 */
function parsePriceToCents(dollars: string): number {
  const parsed = parseFloat(dollars);
  if (isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
}

/**
 * Get billing period display text
 */
function getBillingPeriodDisplay(period: BillingPeriod): string {
  switch (period) {
    case "monthly":
      return "/month";
    case "yearly":
      return "/year";
    case "one_time":
      return " one-time";
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Loading Spinner Component
// ---------------------------------------------------------------------------

function LoadingSpinner() {
  return (
    <div className="flex justify-center items-center py-12">
      <svg
        className="animate-spin h-8 w-8 text-indigo-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span className="sr-only">Loading...</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State Component
// ---------------------------------------------------------------------------

function EmptyState({ onAddClick }: { onAddClick: () => void }) {
  return (
    <div className="text-center py-12">
      <svg
        className="mx-auto h-12 w-12 text-slate-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
        />
      </svg>
      <h3 className="mt-4 text-lg font-medium text-slate-900">No tiers yet</h3>
      <p className="mt-2 text-sm text-slate-600">
        Create subscription tiers for your readers.
      </p>
      <button
        onClick={onAddClick}
        className="mt-6 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
      >
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Create your first tier
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error State Component
// ---------------------------------------------------------------------------

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps) {
  return (
    <div className="text-center py-12">
      <svg
        className="mx-auto h-12 w-12 text-red-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <h3 className="mt-4 text-lg font-medium text-slate-900">
        Failed to load tiers
      </h3>
      <p className="mt-2 text-sm text-slate-600">{message}</p>
      <button
        onClick={onRetry}
        className="mt-6 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
      >
        Retry
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier Card Component
// ---------------------------------------------------------------------------

interface TierCardProps {
  tier: Tier;
  onEdit: (tier: Tier) => void;
  onDelete: (tier: Tier) => void;
}

function TierCard({ tier, onEdit, onDelete }: TierCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-slate-900">
              {tier.name}
            </h3>
            {tier.isDefault && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                default
              </span>
            )}
          </div>
          {tier.description && (
            <p className="mt-1 text-sm text-slate-600">{tier.description}</p>
          )}
        </div>
        <div className="text-right">
          <span className="text-sm text-slate-500">Level: {tier.level}</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-lg font-semibold text-slate-900">
          ${formatPrice(tier.priceCents)}
          <span className="text-sm font-normal text-slate-500">
            {getBillingPeriodDisplay(tier.billingPeriod)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(tier)}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors"
          >
            Edit
          </button>
          {!tier.isDefault && (
            <button
              onClick={() => onDelete(tier)}
              className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier Form Modal Component
// ---------------------------------------------------------------------------

interface TierFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TierFormData) => Promise<void>;
  initialData?: Tier | null;
  isSubmitting: boolean;
  error?: string | null;
}

function TierFormModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isSubmitting,
  error,
}: TierFormModalProps) {
  const isEditing = !!initialData;

  const [formData, setFormData] = useState<TierFormData>({
    name: "",
    slug: "",
    description: "",
    level: 0,
    priceDollars: "0.00",
    billingPeriod: "monthly",
  });

  const [errors, setErrors] = useState<TierFormErrors>({});
  const [slugTouched, setSlugTouched] = useState(false);

  // Reset form when modal opens/closes or initial data changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          name: initialData.name,
          slug: initialData.slug,
          description: initialData.description || "",
          level: initialData.level,
          priceDollars: formatPrice(initialData.priceCents),
          billingPeriod: initialData.billingPeriod,
        });
        setSlugTouched(true);
      } else {
        setFormData({
          name: "",
          slug: "",
          description: "",
          level: 0,
          priceDollars: "0.00",
          billingPeriod: "monthly",
        });
        setSlugTouched(false);
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  // Auto-generate slug from name when creating new tier
  useEffect(() => {
    if (!slugTouched && !isEditing && formData.name) {
      setFormData((prev) => ({
        ...prev,
        slug: slugify(formData.name),
      }));
    }
  }, [formData.name, slugTouched, isEditing]);

  function handleChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value } = e.target;

    if (name === "slug") {
      setSlugTouched(true);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name === "level" ? parseInt(value, 10) || 0 : value,
    }));

    // Clear error for this field
    if (errors[name as keyof TierFormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  function validate(): boolean {
    const newErrors: TierFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.slug.trim()) {
      newErrors.slug = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug =
        "Slug must contain only lowercase letters, numbers, and hyphens";
    }

    if (formData.level < 0) {
      newErrors.level = "Level must be 0 or greater";
    }

    const priceCents = parsePriceToCents(formData.priceDollars);
    if (priceCents < 0) {
      newErrors.priceDollars = "Price cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    await onSubmit(formData);
  }

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          role="dialog"
          aria-modal="true"
        >
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                {isEditing ? "Edit Tier" : "Add Tier"}
              </h2>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Error message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Premium Access"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Slug */}
              <div>
                <label
                  htmlFor="slug"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Slug
                </label>
                <input
                  id="slug"
                  name="slug"
                  type="text"
                  value={formData.slug}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="premium-access"
                />
                {errors.slug && (
                  <p className="mt-1 text-sm text-red-600">{errors.slug}</p>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  URL-safe identifier (lowercase letters, numbers, hyphens)
                </p>
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Early access to new chapters"
                />
              </div>

              {/* Level */}
              <div>
                <label
                  htmlFor="level"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Level
                </label>
                <input
                  id="level"
                  name="level"
                  type="number"
                  min="0"
                  value={formData.level}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {errors.level && (
                  <p className="mt-1 text-sm text-red-600">{errors.level}</p>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  Higher levels grant access to more content (free=0, basic=1,
                  premium=2, etc.)
                </p>
              </div>

              {/* Price */}
              <div>
                <label
                  htmlFor="priceDollars"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Price ($)
                </label>
                <input
                  id="priceDollars"
                  name="priceDollars"
                  type="text"
                  inputMode="decimal"
                  value={formData.priceDollars}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="9.99"
                />
                {errors.priceDollars && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.priceDollars}
                  </p>
                )}
              </div>

              {/* Billing Period */}
              <div>
                <label
                  htmlFor="billingPeriod"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Billing Period
                </label>
                <select
                  id="billingPeriod"
                  name="billingPeriod"
                  value={formData.billingPeriod}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one_time">One-time</option>
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Delete Confirmation Modal Component
// ---------------------------------------------------------------------------

interface DeleteConfirmModalProps {
  isOpen: boolean;
  tier: Tier | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
  error?: string | null;
}

function DeleteConfirmModal({
  isOpen,
  tier,
  onClose,
  onConfirm,
  isDeleting,
  error,
}: DeleteConfirmModalProps) {
  if (!isOpen || !tier) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-sm w-full"
          role="dialog"
          aria-modal="true"
        >
          <div className="p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900 text-center">
              Delete Tier
            </h3>
            <p className="mt-2 text-sm text-slate-600 text-center">
              Are you sure you want to delete "{tier.name}"? Chapters using this
              tier will become free to access.
            </p>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? "Deleting..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function Tiers() {
  // Data state
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingTier, setDeletingTier] = useState<Tier | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch tiers
  const fetchTiers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.tiers.list();
      setTiers(response.tiers);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to load tiers");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  // Open add modal
  function handleAddClick() {
    setEditingTier(null);
    setFormError(null);
    setIsFormModalOpen(true);
  }

  // Open edit modal
  function handleEditClick(tier: Tier) {
    setEditingTier(tier);
    setFormError(null);
    setIsFormModalOpen(true);
  }

  // Close form modal
  function handleFormModalClose() {
    setIsFormModalOpen(false);
    setEditingTier(null);
    setFormError(null);
  }

  // Submit form (create or update)
  async function handleFormSubmit(data: TierFormData) {
    setIsSubmitting(true);
    setFormError(null);

    try {
      const priceCents = parsePriceToCents(data.priceDollars);

      if (editingTier) {
        // Update existing tier
        await api.tiers.update(editingTier.id, {
          name: data.name,
          slug: data.slug,
          description: data.description || null,
          level: data.level,
          priceCents,
          billingPeriod: data.billingPeriod,
        });
      } else {
        // Create new tier
        await api.tiers.create({
          name: data.name,
          slug: data.slug,
          description: data.description || undefined,
          level: data.level,
          priceCents,
          billingPeriod: data.billingPeriod,
        });
      }

      // Refresh list and close modal
      await fetchTiers();
      handleFormModalClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError("Failed to save tier");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // Open delete modal
  function handleDeleteClick(tier: Tier) {
    setDeletingTier(tier);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  }

  // Close delete modal
  function handleDeleteModalClose() {
    setIsDeleteModalOpen(false);
    setDeletingTier(null);
    setDeleteError(null);
  }

  // Confirm delete
  async function handleDeleteConfirm() {
    if (!deletingTier) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await api.tiers.delete(deletingTier.id);
      await fetchTiers();
      handleDeleteModalClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError(err.message);
      } else {
        setDeleteError("Failed to delete tier");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Subscription Tiers
          </h1>
          <p className="mt-1 text-slate-600">
            Manage the tiers readers can subscribe to
          </p>
        </div>
        {!isLoading && !error && (
          <button
            onClick={handleAddClick}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Tier
          </button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchTiers} />
      ) : tiers.length === 0 ? (
        <EmptyState onAddClick={handleAddClick} />
      ) : (
        <div className="space-y-4">
          {tiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      {/* Form Modal */}
      <TierFormModal
        isOpen={isFormModalOpen}
        onClose={handleFormModalClose}
        onSubmit={handleFormSubmit}
        initialData={editingTier}
        isSubmitting={isSubmitting}
        error={formError}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        tier={deletingTier}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        error={deleteError}
      />
    </div>
  );
}
